import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard, A11y, Autoplay, EffectFade, FreeMode, Thumbs } from 'swiper/modules';
import IMask from 'imask';
import { OverlayScrollbars } from 'overlayscrollbars';
import { initReveal } from './reveal';

function initSliders() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('.swiper[data-swiper]').forEach((el) => {
        if (el.classList.contains('swiper-initialized')) {
            return;
        }

        let opts = {};
        try {
            opts = JSON.parse(el.getAttribute('data-swiper') || '{}');
        } catch (e) {
            opts = {};
        }
        if (reduce && opts.autoplay) opts.autoplay = false;

        const root = el.closest('.slider, [data-swiper-root]') || el.parentElement;
        const videoMode = !reduce && !!opts.autoplay && el.querySelector('video[data-slide-video]');
        if (videoMode) opts.autoplay = false;

        const sw = new Swiper(el, {
            modules: [Navigation, Pagination, Keyboard, A11y, Autoplay, EffectFade],
            slidesPerView: 1,
            speed: 500,
            ...opts,
            pagination: {
                el: root.querySelector('.swiper-pagination'),
                clickable: true,
            },
            navigation: {
                prevEl: root.querySelector('[data-swiper-prev]') || root.querySelector('.slider__nav--prev'),
                nextEl: root.querySelector('[data-swiper-next]') || root.querySelector('.slider__nav--next'),
            },
            keyboard: { enabled: true },
            a11y: { enabled: true },
        });

        if (videoMode) attachSliderVideoAutoplay(sw);
    });
}

function attachSliderVideoAutoplay(sw) {
    const DEFAULT_DELAY = 6000;
    let timer = null;
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const next = () => { if (sw.slides.length > 1) sw.slideNext(); };

    const playActive = () => {
        clear();
        sw.el.querySelectorAll('video').forEach((v) => { v.pause(); v.onended = null; });

        const active = sw.el.querySelector('.swiper-slide-active');
        if (!active) return;
        const video = active.querySelector('video');

        if (video) {
            const src = video.getAttribute('data-slide-video');
            if (src && video.getAttribute('src') !== src) {
                video.setAttribute('src', src);
                video.load();
            }
            try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
            video.onended = next;
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
        } else {
            const delay = Number(active.getAttribute('data-slide-delay')) || DEFAULT_DELAY;
            timer = setTimeout(next, delay);
        }
    };

    sw.on('slideChangeTransitionEnd', playActive);
    playActive();
}

function initLemonadeAssortment() {
    const root = document.querySelector('[data-lemo-assortment]');
    if (!root) return;

    const el = root.querySelector('.swiper');
    const wrapper = el.querySelector('.swiper-wrapper');
    const pagination = root.querySelector('.swiper-pagination');
    const prevEl = root.querySelector('[data-swiper-prev]');
    const nextEl = root.querySelector('[data-swiper-next]');
    const originals = Array.from(wrapper.children);
    const desktopMq = window.matchMedia('(min-width: 1024px)');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = reduce ? false : { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true };
    let sw = null;

    const build = () => {
        if (sw) {
            sw.destroy(true, true);
            sw = null;
        }
        originals.forEach((s) => s.removeAttribute('aria-hidden'));
        wrapper.replaceChildren(...originals);

        if (desktopMq.matches) {
            for (let p = 0; p < 2; p++) {
                originals.forEach((s) => {
                    const clone = s.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    wrapper.appendChild(clone);
                });
            }
            sw = new Swiper(el, {
                modules: [Navigation, Autoplay, Keyboard, A11y],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                loopAdditionalSlides: 2,
                speed: 600,
                autoplay,
                navigation: { prevEl, nextEl },
                keyboard: { enabled: true },
                a11y: { enabled: true },
            });
        } else {
            sw = new Swiper(el, {
                modules: [Pagination, Autoplay, A11y],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 12,
                loop: true,
                speed: 600,
                autoplay,
                pagination: { el: pagination, clickable: true },
                a11y: { enabled: true },
            });
        }
    };

    build();
    desktopMq.addEventListener('change', build);
}

function initHeroSlider() {
    const el = document.querySelector('[data-hero-swiper]');
    if (!el || el.classList.contains('swiper-initialized')) return;

    const root = el.closest('[data-swiper-root]') || el.parentElement;
    const single = el.querySelectorAll('.swiper-slide').length < 2;
    const DEFAULT_IMAGE_DELAY = 5000;

    const mobileMq = window.matchMedia('(max-width: 767.98px)');
    const tabletMq = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)');
    const heroSources = (v) => {
        if (mobileMq.matches && v.dataset.heroVideoMobile) {
            return { src: v.dataset.heroVideoMobile, poster: v.dataset.heroPosterMobile };
        }
        if (tabletMq.matches && v.dataset.heroVideoTablet) {
            return { src: v.dataset.heroVideoTablet, poster: v.dataset.heroPosterTablet };
        }
        return { src: v.dataset.heroVideo, poster: v.dataset.heroPoster };
    };
    const syncVideoSrc = (v) => {
        const want = heroSources(v);
        if (want.poster && v.getAttribute('poster') !== want.poster) {
            v.setAttribute('poster', want.poster);
        }
        if (want.src && v.getAttribute('src') !== want.src) {
            v.setAttribute('src', want.src);
            v.load();
        }
    };

    const READY_TIMEOUT = 4000;
    let timer = null;
    let waiting = null;
    const clearTimer = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };
    const clearWait = () => {
        if (waiting) {
            waiting();
            waiting = null;
        }
    };

    const videoAt = (sw, i) => sw.slides[i] && sw.slides[i].querySelector('video');
    const nextIndex = (sw) => (sw.activeIndex + 1) % sw.slides.length;

    // give the upcoming clip a head start so the crossfade never lands on an empty <video>
    const primeNext = (sw) => {
        const v = videoAt(sw, nextIndex(sw));
        if (v) syncVideoSrc(v);
    };

    const advance = (sw) => {
        if (single) return;
        const v = videoAt(sw, nextIndex(sw));
        if (!v || v.readyState >= 3) {
            sw.slideNext();
            return;
        }
        syncVideoSrc(v);
        clearWait();
        const go = () => {
            clearWait();
            sw.slideNext();
        };
        const bail = setTimeout(go, READY_TIMEOUT);
        v.addEventListener('canplay', go, { once: true });
        waiting = () => {
            clearTimeout(bail);
            v.removeEventListener('canplay', go);
        };
    };

    // called at transition START so the incoming clip is already running while it fades in
    const playActive = (sw) => {
        clearTimer();
        clearWait();
        const active = sw.slides[sw.activeIndex];
        if (!active) return;
        sw.slides.forEach((s) => {
            const v = s.querySelector('video');
            if (v && s !== active) v.onended = null;
        });

        const video = active.querySelector('video');
        if (video) {
            video.loop = single;
            syncVideoSrc(video);
            try {
                video.currentTime = 0;
            } catch (e) { /* not seekable yet */
            }
            if (!single) video.onended = () => advance(sw);
            const p = video.play();
            if (p && p.catch) p.catch(() => {
            });
        } else {
            const delay = Number(active.getAttribute('data-hero-delay')) || DEFAULT_IMAGE_DELAY;
            if (!single) timer = setTimeout(() => advance(sw), delay);
        }
    };

    // pause only once the fade is over, so the outgoing clip never freezes mid-crossfade
    const pauseInactive = (sw) => {
        sw.slides.forEach((s, i) => {
            const v = s.querySelector('video');
            if (v && i !== sw.activeIndex) v.pause();
        });
        primeNext(sw);
    };

    const sw = new Swiper(el, {
        modules: [EffectFade, Navigation, Pagination, Keyboard, A11y],
        slidesPerView: 1,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: 800,
        rewind: !single,
        allowTouchMove: !single,
        pagination: { el: root.querySelector('.swiper-pagination'), clickable: true },
        navigation: {
            prevEl: root.querySelector('[data-swiper-prev]'),
            nextEl: root.querySelector('[data-swiper-next]'),
        },
        keyboard: { enabled: true },
        a11y: { enabled: true },
        on: {
            init: (sw) => {
                playActive(sw);
                primeNext(sw);
            },
            slideChangeTransitionStart: (sw) => playActive(sw),
            slideChangeTransitionEnd: (sw) => pauseInactive(sw),
        },
    });

    const reload = () => {
        playActive(sw);
        primeNext(sw);
    };
    mobileMq.addEventListener('change', reload);
    tabletMq.addEventListener('change', reload);
}

function initBrandsMarquee() {
    const el = document.querySelector('[data-brands-marquee]');
    if (!el) return;

    const wrapper = el.querySelector('.swiper-wrapper');
    const pagination = el.querySelector('[data-brands-pagination]');
    const originals = Array.from(wrapper.children);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const desktopMq = window.matchMedia('(min-width: 768px)');
    let sw = null;

    const build = () => {
        if (sw) {
            sw.destroy(true, true);
            sw = null;
        }
        originals.forEach((s) => {
            s.removeAttribute('aria-hidden');
            s.removeAttribute('tabindex');
        });
        wrapper.replaceChildren(...originals);

        if (desktopMq.matches) {
            for (let p = 0; p < 2; p++) {
                originals.forEach((s) => {
                    const clone = s.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    clone.setAttribute('tabindex', '-1');
                    wrapper.appendChild(clone);
                });
            }
            sw = new Swiper(el, {
                modules: [FreeMode, Autoplay, A11y],
                slidesPerView: 'auto',
                spaceBetween: 16,
                loop: true,
                loopPreventsSliding: false,
                grabCursor: true,
                allowTouchMove: true,
                speed: 6000,
                freeMode: { enabled: true, momentum: false },
                autoplay: reduce ? false : { delay: 0, disableOnInteraction: false },
                a11y: { enabled: true },
                breakpoints: { 1024: { spaceBetween: 22 } },
            });
            // resume after click/drag release
            const inst = sw;
            const resume = () => requestAnimationFrame(() => {
                if (!inst.destroyed && inst.autoplay.running && inst.autoplay.paused) inst.autoplay.resume();
            });
            inst.on('touchStart', () => {
                document.addEventListener('pointerup', resume, { once: true });
                document.addEventListener('pointercancel', resume, { once: true });
            });
        } else {
            sw = new Swiper(el, {
                modules: [Pagination, Autoplay, A11y],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                grabCursor: true,
                autoplay: reduce ? false : { delay: 3500, disableOnInteraction: false },
                a11y: { enabled: true },
                pagination: { el: pagination, clickable: true },
            });
        }
    };

    build();
    desktopMq.addEventListener('change', build);
}

function initTeamCarousel() {
    document.querySelectorAll('[data-team-carousel]').forEach(buildTeamCarousel);
}

function buildTeamCarousel(root) {
    const el = root.querySelector('.swiper');
    const wrapper = el.querySelector('.swiper-wrapper');
    const pagination = root.querySelector('[data-team-pagination]');
    const prevEl = root.querySelector('[data-swiper-prev]');
    const nextEl = root.querySelector('[data-swiper-next]');
    const originals = Array.from(wrapper.children);
    const desktopMq = window.matchMedia('(min-width: 1024px)');
    let sw = null;

    const build = () => {
        if (sw) {
            sw.destroy(true, true);
            sw = null;
        }
        originals.forEach((s) => s.removeAttribute('aria-hidden'));
        wrapper.replaceChildren(...originals);

        if (desktopMq.matches) {
            for (let p = 0; p < 2; p++) {
                originals.forEach((s) => {
                    const clone = s.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    wrapper.appendChild(clone);
                });
            }
            sw = new Swiper(el, {
                modules: [Navigation, Keyboard, A11y],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                loopAdditionalSlides: 2,
                speed: 500,
                navigation: { prevEl, nextEl },
                keyboard: { enabled: true },
                a11y: { enabled: true },
            });
        } else {
            sw = new Swiper(el, {
                modules: [Pagination, A11y],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                speed: 500,
                pagination: { el: pagination, clickable: true },
                a11y: { enabled: true },
            });
        }
    };

    build();
    desktopMq.addEventListener('change', build);
}

function initStoriesCarousel() {
    const root = document.querySelector('[data-stories-carousel]');
    if (!root) return;

    const el = root.querySelector('.swiper');
    const wrapper = el.querySelector('.swiper-wrapper');
    const pagination = root.querySelector('[data-stories-pagination]');
    const prevEl = root.querySelector('[data-swiper-prev]');
    const nextEl = root.querySelector('[data-swiper-next]');
    const originals = Array.from(wrapper.children);
    const desktopMq = window.matchMedia('(min-width: 1024px)');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = (root.hasAttribute('data-autoplay') && !reduce)
        ? { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false;
    let sw = null;

    const build = () => {
        if (sw) {
            sw.destroy(true, true);
            sw = null;
        }
        originals.forEach((s) => s.removeAttribute('aria-hidden'));
        wrapper.replaceChildren(...originals);

        if (desktopMq.matches) {
            for (let p = 0; p < 2; p++) {
                originals.forEach((s) => {
                    const clone = s.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    wrapper.appendChild(clone);
                });
            }
            sw = new Swiper(el, {
                modules: [Navigation, Keyboard, A11y, Autoplay],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                loopAdditionalSlides: 2,
                speed: 500,
                autoplay,
                navigation: { prevEl, nextEl },
                keyboard: { enabled: true },
                a11y: { enabled: true },
            });
        } else {
            sw = new Swiper(el, {
                modules: [Pagination, A11y, Autoplay],
                slidesPerView: 'auto',
                centeredSlides: true,
                spaceBetween: 22,
                loop: true,
                speed: 500,
                autoplay,
                pagination: { el: pagination, clickable: true },
                a11y: { enabled: true },
            });
        }
    };

    build();
    desktopMq.addEventListener('change', build);
}

function initPeopleGallery() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = reduce ? false : { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true };

    document.querySelectorAll('[data-people-gallery]').forEach((gallery) => {
        const mainEl = gallery.querySelector('.people-feature__main');
        if (!mainEl || mainEl.classList.contains('swiper-initialized')) return;

        const thumbsEl = gallery.querySelector('.people-feature__thumbs');
        let thumbsSwiper = null;

        if (thumbsEl) {
            thumbsSwiper = new Swiper(thumbsEl, {
                modules: [FreeMode, A11y],
                slidesPerView: 3,
                spaceBetween: 14,
                freeMode: true,
                watchSlidesProgress: true,
                observer: true,
                observeParents: true,
                a11y: { enabled: true },
                breakpoints: { 768: { slidesPerView: 5 } },
            });
        }

        new Swiper(mainEl, {
            modules: [Pagination, Thumbs, Autoplay, Keyboard, A11y],
            slidesPerView: 1,
            speed: 500,
            observer: true,
            observeParents: true,
            autoplay,
            pagination: { el: mainEl.querySelector('.swiper-pagination'), clickable: true },
            thumbs: { swiper: thumbsSwiper },
            keyboard: { enabled: true },
            a11y: { enabled: true },
        });
    });
}

function initProductCarousel() {
    document.querySelectorAll('[data-product-carousel]').forEach((root) => {
        const el = root.querySelector('[data-product-carousel-swiper]');
        if (!el || el.classList.contains('swiper-initialized')) return;

        const wrapper = el.querySelector('.swiper-wrapper');
        const prevEl = root.querySelector('[data-product-carousel-prev]');
        const nextEl = root.querySelector('[data-product-carousel-next]');
        const panel = root.querySelector('[data-product-carousel-panel]');
        const titleEl = root.querySelector('[data-product-carousel-title]');
        const leadEl = root.querySelector('[data-product-carousel-lead]');
        const textEl = root.querySelector('[data-product-carousel-text]');
        const bullets = Array.from(root.querySelectorAll('[data-product-carousel-bullet]'));
        const originals = Array.from(wrapper.children);

        // loop + centered + auto slidesPerView needs enough real slides, so
        // pre-duplicate (Swiper 12 reorders real DOM, no auto-clones here).
        // Real (non-clone) slides stay at indices 0..originals.length-1.
        for (let p = 0; p < 2; p++) {
            originals.forEach((slide) => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                wrapper.appendChild(clone);
            });
        }

        let sw = null;

        const applySlide = (s) => {
            const slide = s.slides[s.activeIndex];
            if (!slide) return;
            if (panel) {
                panel.style.background = slide.getAttribute('data-bg') || '';
                panel.style.color = slide.getAttribute('data-text-color') || '';
            }
            if (titleEl) titleEl.textContent = slide.getAttribute('data-title') || '';
            if (leadEl) leadEl.textContent = slide.getAttribute('data-lead') || '';
            if (textEl) textEl.textContent = slide.getAttribute('data-text') || '';
            const idx = bullets.length ? s.realIndex % bullets.length : 0;
            bullets.forEach((b, i) => b.classList.toggle('is-active', i === idx));
        };

        bullets.forEach((btn, i) => {
            btn.addEventListener('click', () => {
                if (sw) sw.slideToLoop(i);
            });
        });

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let opts = {};
        try {
            opts = JSON.parse(el.getAttribute('data-product-carousel-swiper') || '{}');
        } catch (e) {
            opts = {};
        }
        if (reduce && opts.autoplay) opts.autoplay = false;

        sw = new Swiper(el, {
            modules: [Navigation, Autoplay, Keyboard, A11y],
            slidesPerView: 'auto',
            centeredSlides: true,
            loop: true,
            loopAdditionalSlides: 2,
            speed: 500,
            spaceBetween: 20,
            breakpoints: { 1024: { spaceBetween: 60 } },
            ...opts,
            navigation: { prevEl, nextEl },
            keyboard: { enabled: true },
            a11y: { enabled: true },
            on: {
                init: applySlide,
                slideChange: applySlide,
            },
        });

        if (panel) {
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (reduce) panel.style.transition = 'none';
        }
    });
}

function initTestimonialModal() {
    const modal = document.querySelector('[data-testimonial-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.testimonial-modal__dialog');
    const avatarBox = modal.querySelector('[data-testimonial-avatar]');
    const nameEl = modal.querySelector('[data-testimonial-name]');
    const roleEl = modal.querySelector('[data-testimonial-role]');
    const textEl = modal.querySelector('[data-testimonial-text]');
    let lastFocused = null;

    const open = (card) => {
        if (!card) return;
        const img = card.querySelector('.story-card__avatar img');
        avatarBox.innerHTML = '';
        if (img) {
            const clone = img.cloneNode(true);
            clone.removeAttribute('width');
            clone.removeAttribute('height');
            avatarBox.appendChild(clone);
        }
        nameEl.textContent = card.getAttribute('data-name') || '';
        roleEl.textContent = card.getAttribute('data-role') || '';
        const text = card.querySelector('.story-card__text');
        textEl.innerHTML = text ? text.innerHTML : '';

        lastFocused = document.activeElement;
        modal.hidden = false;
        void modal.offsetWidth;
        modal.classList.add('is-open');
        document.body.classList.add('modal-open');
        dialog.focus();
    };

    const close = () => {
        if (!modal.classList.contains('is-open')) return;
        modal.classList.remove('is-open');
        document.body.classList.remove('modal-open');
        setTimeout(() => {
            modal.hidden = true;
        }, 250);
        if (lastFocused && lastFocused.focus) lastFocused.focus();
    };

    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-testimonial-open]');
        if (trigger) {
            open(trigger.closest('.story-card'));
            return;
        }
        if (e.target.closest('[data-testimonial-close]')) close();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
    });
}

function initHistoryModal() {
    const modal = document.querySelector('[data-history-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.history-modal__dialog');
    const yearEl = modal.querySelector('[data-history-year]');
    const textEl = modal.querySelector('[data-history-text]');
    let lastFocused = null;

    const open = (card) => {
        if (!card) return;
        const year = card.querySelector('.history-card__year');
        const text = card.querySelector('.history-card__text');
        yearEl.textContent = year ? year.textContent : '';
        textEl.innerHTML = text ? text.innerHTML : '';

        lastFocused = document.activeElement;
        modal.hidden = false;
        void modal.offsetWidth;
        modal.classList.add('is-open');
        document.body.classList.add('modal-open');
        dialog.focus();
    };

    const close = () => {
        if (!modal.classList.contains('is-open')) return;
        modal.classList.remove('is-open');
        document.body.classList.remove('modal-open');
        setTimeout(() => {
            modal.hidden = true;
        }, 250);
        if (lastFocused && lastFocused.focus) lastFocused.focus();
    };

    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-history-open]');
        if (trigger) {
            open(trigger.closest('.history-card'));
            return;
        }
        if (e.target.closest('[data-history-close]')) close();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
    });
}

function initBenefitsSlider() {
    const root = document.querySelector('[data-benefits]');
    if (!root) return;

    const el = root.querySelector('[data-benefits-swiper]');
    const scrollEl = root.querySelector('[data-benefits-scroll]');
    if (!el) return;

    const items = Array.from(root.querySelectorAll('.benefits__item'));
    const buttons = Array.from(root.querySelectorAll('[data-benefits-item]'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const desktopMq = window.matchMedia('(min-width: 1024px)');

    let os = null;

    const scrollActiveIntoView = (index) => {
        if (!desktopMq.matches) return;
        const li = items[index];
        if (!li) return;
        const viewport = os ? os.elements().viewport : scrollEl;
        if (!viewport) return;
        const top = li.offsetTop;
        const bottom = top + li.offsetHeight;
        if (top < viewport.scrollTop) {
            viewport.scrollTo({ top, behavior: 'smooth' });
        } else if (bottom > viewport.scrollTop + viewport.clientHeight) {
            viewport.scrollTo({ top: bottom - viewport.clientHeight, behavior: 'smooth' });
        }
    };

    const setActive = (index) => {
        items.forEach((li, i) => li.classList.toggle('is-active', i === index));
        scrollActiveIntoView(index);
    };

    const sw = new Swiper(el, {
        modules: [Pagination, Autoplay, Keyboard, A11y],
        slidesPerView: 1,
        speed: 500,
        loop: true,
        observer: true,
        observeParents: true,
        pagination: { el: root.querySelector('[data-benefits-dots]'), clickable: true },
        autoplay: reduce ? false : { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        keyboard: { enabled: true },
        a11y: { enabled: true },
        on: {
            init: (s) => setActive(s.realIndex),
            realIndexChange: (s) => setActive(s.realIndex),
        },
    });

    buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
            sw.slideToLoop(Number(btn.getAttribute('data-benefits-item')) || 0);
        });
    });

    const buildScroll = () => {
        if (desktopMq.matches && !os && scrollEl) {
            os = OverlayScrollbars(scrollEl, { scrollbars: { autoHide: 'leave', autoHideDelay: 300 } });
        } else if (!desktopMq.matches && os) {
            os.destroy();
            os = null;
        }
    };

    buildScroll();
    desktopMq.addEventListener('change', buildScroll);
}

function initPhoneMasks() {
    document.querySelectorAll('[data-phone-mask]').forEach((el) => {
        if (el.dataset.maskInit) return;
        el.dataset.maskInit = '1';
        const mask = IMask(el, { mask: '+{38} (000) 000-00-00', lazy: true });
        el.addEventListener('focus', () => mask.updateOptions({ lazy: false }));
        el.addEventListener('blur', () => {
            if (!mask.unmaskedValue) mask.updateOptions({ lazy: true });
        });
    });
}

function initHeaderScroll() {
    const header = document.getElementById('site-header');
    if (!header) return;

    const overlay = document.body.classList.contains('page-index')
        || document.body.classList.contains('header-overlay');
    let last = Math.max(window.scrollY, 0);
    let ticking = false;

    const update = () => {
        ticking = false;
        const y = Math.max(window.scrollY, 0);

        if (overlay) header.classList.toggle('header--scrolled', y > 90);

        // menu overlay lives inside header — never hide it while open
        if (document.body.classList.contains('menu-open')) {
            header.classList.remove('header--hidden');
            last = y;
            return;
        }

        const delta = y - last;
        if (Math.abs(delta) > 5) {
            header.classList.toggle('header--hidden', delta > 0 && y > header.offsetHeight + 40);
            last = y;
        }
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
}

function initScrollNext() {
    document.querySelectorAll('[data-scroll-next]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            const href = btn.getAttribute('href') || '';
            const target = href.length > 1 && href.startsWith('#') ? document.querySelector(href) : null;
            const dest = target || btn.closest('section')?.nextElementSibling;
            if (!dest) return;
            e.preventDefault();
            dest.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });
}

function initStatsCounters() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (el) => {
        const raw = el.textContent.trim();
        const m = raw.match(/\d[\d\s]*\d|\d/);
        if (!m) return;
        const prefix = raw.slice(0, m.index);
        const suffix = raw.slice(m.index + m[0].length);
        const target = parseInt(m[0].replace(/\s+/g, ''), 10);
        if (reduce || !target) return;

        const duration = 2000;
        let start = null;
        const step = (now) => {
            if (start === null) start = now;
            const p = Math.min((now - start) / duration, 1);
            el.textContent = prefix + Math.round(ease(p) * target) + suffix;
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = raw;
        };
        el.textContent = prefix + '0' + suffix;
        requestAnimationFrame(step);
    };

    const configs = [
        ['.home-stats-v2', '.home-stats-v2__value'],
        ['.trust', '.trust__num'],
    ];

    configs.forEach(([rootSel, valSel]) => {
        const section = document.querySelector(rootSel);
        if (!section) return;
        const io = new IntersectionObserver((entries, obs) => {
            if (entries.some((e) => e.isIntersecting)) {
                section.querySelectorAll(valSel).forEach(animate);
                obs.disconnect();
            }
        }, { threshold: 0.3 });
        io.observe(section);
    });
}

function initMobileMenu() {
    const menu = document.querySelector('[data-menu]');
    const openBtn = document.querySelector('[data-menu-open]');
    if (!menu || !openBtn) return;

    let closeTimer = null;

    const open = () => {
        if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
        }
        menu.hidden = false;
        void menu.offsetWidth; // force reflow so opacity transition triggers
        menu.classList.add('is-open');
        document.body.classList.add('menu-open');
        openBtn.setAttribute('aria-expanded', 'true');
    };

    const close = (returnFocus = true) => {
        menu.classList.remove('is-open');
        document.body.classList.remove('menu-open');
        openBtn.setAttribute('aria-expanded', 'false');
        closeTimer = setTimeout(() => {
            menu.hidden = true;
        }, 300);
        if (returnFocus) openBtn.focus();
    };

    openBtn.addEventListener('click', open);
    menu.querySelectorAll('[data-menu-close]').forEach((el) => el.addEventListener('click', () => close()));
    menu.querySelectorAll('[data-menu-link]').forEach((el) => el.addEventListener('click', () => close(false)));

    menu.querySelectorAll('.mobile-menu__toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
            const item = btn.closest('.mobile-menu__item');
            const expanded = item.classList.toggle('mobile-menu__item--expanded');
            btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
    });

    // close + release scroll-lock when crossing into desktop nav
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => {
        if (e.matches && menu.classList.contains('is-open')) close(false);
    });
}

// baked hero video: pick src/poster per breakpoint (matches the <picture> breakpoints)
function initBakedVideo() {
    const videos = document.querySelectorAll('video[data-video]');
    if (!videos.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileMq = window.matchMedia('(max-width: 650px)');
    const tabletMq = window.matchMedia('(max-width: 999px)');

    const pick = (v) => {
        if (mobileMq.matches && v.dataset.videoMobile) {
            return { src: v.dataset.videoMobile, poster: v.dataset.posterMobile };
        }
        if (tabletMq.matches && v.dataset.videoTablet) {
            return { src: v.dataset.videoTablet, poster: v.dataset.posterTablet };
        }
        return { src: v.dataset.video, poster: v.dataset.poster };
    };

    const sync = (v) => {
        const want = pick(v);
        if (want.poster && v.getAttribute('poster') !== want.poster) {
            v.setAttribute('poster', want.poster);
        }
        if (reduce || !want.src || v.getAttribute('src') === want.src) return;
        v.setAttribute('src', want.src);
        v.load();
        const p = v.play();
        if (p && p.catch) p.catch(() => { /* autoplay blocked — poster stays */ });
    };

    const syncAll = () => videos.forEach(sync);
    syncAll();
    mobileMq.addEventListener('change', syncAll);
    tabletMq.addEventListener('change', syncAll);
}

function initFlipInView() {
    const roots = [...document.querySelectorAll('[data-flip-inview]')];
    if (!roots.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mq = window.matchMedia('(hover: none), (max-width: 1023.98px)');
    const ratios = new Map();
    let io = null;
    let active = [];

    const setActive = (cards) => {
        if (cards.length === active.length && cards.every((c, i) => c === active[i])) return;
        active.forEach((c) => c.classList.remove('is-flipped'));
        active = cards;
        active.forEach((c) => c.classList.add('is-flipped'));
    };

    const build = () => {
        if (io) return;
        io = new IntersectionObserver((entries) => {
            entries.forEach((e) => ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0));
            let best = null;
            let bestRatio = 0;
            ratios.forEach((r, el) => {
                if (r > bestRatio) {
                    bestRatio = r;
                    best = el;
                }
            });
            if (!best) return setActive([]);
            // flip the whole row, not just one card (2-col tablet grids)
            const top = best.getBoundingClientRect().top;
            const row = [...ratios.keys()].filter(
                (el) => ratios.get(el) > 0 && Math.abs(el.getBoundingClientRect().top - top) < 4,
            );
            setActive(row);
        }, { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });

        roots.forEach((root) => {
            const sel = root.getAttribute('data-flip-inview') || '.brand-card';
            root.querySelectorAll(sel).forEach((card) => io.observe(card));
        });
    };

    const destroy = () => {
        if (io) io.disconnect();
        io = null;
        ratios.clear();
        setActive([]);
    };

    const sync = () => (mq.matches ? build() : destroy());
    sync();
    mq.addEventListener('change', sync);
}

function initBabyScheme() {
    document.querySelectorAll('[data-baby-scheme]').forEach((scheme) => {
        const modal = scheme.querySelector('[data-baby-modal]');
        if (!modal) return;
        const cards = modal.querySelectorAll('[data-baby-card]');
        let lastFocused = null;
        let hideTimer = null;

        const open = (index) => {
            const card = modal.querySelector(`[data-baby-card="${index}"]`);
            if (!card) return;
            clearTimeout(hideTimer);
            lastFocused = document.activeElement;
            modal.hidden = false;
            void modal.offsetWidth;
            modal.classList.add('is-open');
            cards.forEach((c) => c.classList.toggle('is-active', c === card));
            document.body.classList.add('modal-open');
            card.focus({ preventScroll: true });
        };

        const close = () => {
            if (!modal.classList.contains('is-open')) return;
            modal.classList.remove('is-open');
            cards.forEach((c) => c.classList.remove('is-active'));
            document.body.classList.remove('modal-open');
            hideTimer = setTimeout(() => { modal.hidden = true; }, 700);
            if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
        };

        scheme.addEventListener('click', (e) => {
            const stage = e.target.closest('[data-baby-stage]');
            if (stage) {
                open(stage.getAttribute('data-baby-stage'));
                return;
            }
            if (e.target.closest('[data-baby-close]') || e.target === modal) close();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') close();
        });
    });
}

const MIRG_VIDEO_MQ = [
    { mq: '(max-width: 666px)', key: 'vert' },
    { mq: '(max-width: 1024px)', key: 'sq' },
];

function mirgVideoKey() {
    const hit = MIRG_VIDEO_MQ.find((v) => window.matchMedia(v.mq).matches);
    return hit ? hit.key : 'hor';
}

function mirgApplyVideoSources(root) {
    const key = mirgVideoKey();
    root.dataset.mirgVideoKey = key;
    root.querySelectorAll('video[data-src-hor]').forEach((video) => {
        const src = video.getAttribute(`data-src-${key}`);
        const poster = video.getAttribute(`data-poster-${key}`);
        video.setAttribute('data-slide-video', src);
        if (poster) video.setAttribute('poster', poster);
    });
}

function prepareMirgorodskaVideos() {
    document.querySelectorAll('[data-mirg-videos]').forEach(mirgApplyVideoSources);
}

function initMirgorodskaVideos() {
    document.querySelectorAll('[data-mirg-videos]').forEach((block) => {
        const swiperEl = block.querySelector('.swiper');
        const sw = swiperEl && swiperEl.swiper;
        if (!sw) return;

        const fill = block.querySelector('[data-mirg-progress]');
        const counter = block.querySelector('[data-mirg-counter]');
        const sound = block.querySelector('[data-mirg-sound]');
        const videos = [...block.querySelectorAll('video')];
        let muted = true;

        const activeVideo = () => sw.slides[sw.activeIndex] && sw.slides[sw.activeIndex].querySelector('video');

        videos.forEach((video) => {
            video.addEventListener('timeupdate', () => {
                if (video !== activeVideo() || !fill || !video.duration) return;
                fill.style.width = `${(video.currentTime / video.duration) * 100}%`;
            });
        });

        sw.on('slideChange', () => {
            if (fill) fill.style.width = '0%';
            if (counter) counter.textContent = String(sw.realIndex + 1);
        });

        block.querySelector('[data-mirg-prev]')?.addEventListener('click', () => sw.slidePrev());
        block.querySelector('[data-mirg-next]')?.addEventListener('click', () => sw.slideNext());

        const setMuted = (value) => {
            muted = value;
            videos.forEach((v) => { v.muted = muted; });
            if (!sound) return;
            sound.setAttribute('aria-pressed', muted ? 'false' : 'true');
            sound.setAttribute('aria-label', muted ? 'Увімкнути звук' : 'Вимкнути звук');
        };

        const playSafe = (video) => {
            const p = video.play();
            if (!p || !p.catch) return;
            p.catch(() => {
                if (video.muted) return;
                setMuted(true);
                const retry = video.play();
                if (retry && retry.catch) retry.catch(() => {});
            });
        };

        if (sound) sound.addEventListener('click', () => setMuted(!muted));

        sw.on('slideChangeTransitionEnd', () => {
            const video = activeVideo();
            if (video && video.getAttribute('src')) playSafe(video);
        });

        const onBreakpoint = () => {
            if (mirgVideoKey() === block.dataset.mirgVideoKey) return;
            mirgApplyVideoSources(block);
            const video = activeVideo();
            if (!video) return;
            const src = video.getAttribute('data-slide-video');
            if (video.getAttribute('src') && video.getAttribute('src') !== src) {
                if (fill) fill.style.width = '0%';
                video.setAttribute('src', src);
                video.load();
                playSafe(video);
            }
        };
        MIRG_VIDEO_MQ.forEach((v) => window.matchMedia(v.mq).addEventListener('change', onBreakpoint));
        window.addEventListener('load', onBreakpoint);
        onBreakpoint();
    });
}

function initMirgorodskaBenefits() {
    document.querySelectorAll('[data-mirg-benefits]').forEach((block) => {
        let current = null;
        let lastFocused = null;
        const hideTimers = new Map();

        const close = () => {
            if (!current) return;
            const modal = current;
            modal.classList.remove('is-open');
            document.body.classList.remove('modal-open');
            hideTimers.set(modal, setTimeout(() => {
                modal.hidden = true;
                hideTimers.delete(modal);
            }, 250));
            current = null;
            if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
        };

        const open = (index) => {
            const modal = block.querySelector(`[data-mirg-modal="${index}"]`);
            if (!modal) return;
            if (current) close();
            clearTimeout(hideTimers.get(modal));
            hideTimers.delete(modal);
            lastFocused = document.activeElement;
            modal.hidden = false;
            void modal.offsetWidth;
            modal.classList.add('is-open');
            document.body.classList.add('modal-open');
            current = modal;
            modal.querySelector('[role="dialog"]')?.focus({ preventScroll: true });
        };

        block.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-mirg-open]');
            if (trigger) {
                open(trigger.getAttribute('data-mirg-open'));
                return;
            }
            if (e.target.closest('[data-mirg-close]')) close();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') close();
        });
    });
}

function initMirgorodskaAssortment() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 1025px)');
    const DURATION = 900;
    const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

    document.querySelectorAll('[data-mirg-assortment]').forEach((block) => {
        const list = block.querySelector('.mirg-assortment__list');
        const items = [...block.querySelectorAll('[data-mirg-item]')];
        let active = [];

        const setOpen = (target) => {
            items.forEach((it) => {
                const on = it === target;
                it.classList.toggle('is-open', on);
                it.querySelector('[data-mirg-toggle]')?.setAttribute('aria-expanded', on ? 'true' : 'false');
            });
        };

        const reset = (el) => {
            const panel = el.querySelector('.mirg-assortment__panel');
            el.classList.remove('is-closing');
            el.style.flex = '';
            el.style.overflow = '';
            panel.style.width = '';
            panel.style.minWidth = '';
        };

        block.addEventListener('click', (e) => {
            const toggle = e.target.closest('[data-mirg-toggle]');
            if (!toggle) return;
            const item = toggle.closest('[data-mirg-item]');
            if (item.classList.contains('is-open')) return;

            active.forEach((a) => a.cancel());
            active = [];
            items.forEach(reset);

            const prev = items.find((it) => it.classList.contains('is-open'));
            const wide = desktop.matches;
            const prop = wide ? 'width' : 'height';
            const size = (el) => el.getBoundingClientRect()[prop];

            const before = new Map(items.map((it) => [it, size(it)]));
            const listBefore = list.getBoundingClientRect().height;
            setOpen(item);
            if (reduce.matches || !prev) return;
            const after = new Map(items.map((it) => [it, size(it)]));
            const listAfter = list.getBoundingClientRect().height;

            prev.classList.add('is-closing');

            const run = (el, from, to, fadeIn) => {
                const panel = el.querySelector('.mirg-assortment__panel');
                if (wide) {
                    const locked = Math.max(from, to);
                    panel.style.width = `${locked}px`;
                    panel.style.minWidth = `${locked}px`;
                    el.style.flex = '0 0 auto';
                }
                el.style.overflow = 'hidden';

                const anim = el.animate(
                    [{ [prop]: `${from}px` }, { [prop]: `${to}px` }],
                    { duration: DURATION, easing: EASING }
                );
                let done = false;
                const finish = () => {
                    if (done) return;
                    done = true;
                    clearTimeout(timer);
                    reset(el);
                };
                const timer = setTimeout(finish, DURATION + 100);
                anim.onfinish = finish;
                anim.oncancel = finish;
                active.push(anim);

                const frames = fadeIn ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }];
                [...panel.children].forEach((child) => {
                    active.push(child.animate(frames, {
                        duration: fadeIn ? DURATION : DURATION * 0.5,
                        easing: 'ease',
                        fill: fadeIn ? 'none' : 'forwards',
                    }));
                });
            };

            run(prev, before.get(prev), after.get(prev), false);
            run(item, before.get(item), after.get(item), true);

            if (wide && Math.abs(listBefore - listAfter) > 1) {
                active.push(list.animate(
                    [{ height: `${listBefore}px` }, { height: `${listAfter}px` }],
                    { duration: DURATION, easing: EASING }
                ));
            }
        });
    });
}

function initMirgorodskaUpdate() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('[data-mirg-update] video').forEach((v) => {
        v.removeAttribute('autoplay');
        v.pause();
    });
}

function init() {
    prepareMirgorodskaVideos();
    initSliders();
    initHeroSlider();
    initBrandsMarquee();
    initTeamCarousel();
    initStoriesCarousel();
    initPeopleGallery();
    initProductCarousel();
    initTestimonialModal();
    initHistoryModal();
    initBenefitsSlider();
    initScrollNext();
    initPhoneMasks();
    initHeaderScroll();
    initStatsCounters();
    initReveal();
    initLemonadeAssortment();
    initBakedVideo();
    initFlipInView();
    initMobileMenu();
    initBabyScheme();
    initMirgorodskaVideos();
    initMirgorodskaBenefits();
    initMirgorodskaAssortment();
    initMirgorodskaUpdate();
}

if (document.readyState !== 'loading') {
    init();
} else {
    document.addEventListener('DOMContentLoaded', init);
}
